import { CalendarDate } from "@internationalized/date";
import type { DateCustomFieldType } from "@repo/app-types/entities/customField";
import type { CustomFieldCondition } from "@repo/app-types/entities/filterConditions";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import {
  CalendarMonth,
  CalendarNavigationHeader,
} from "@repo/ui/dateRange/calendar";
import { Size } from "@repo/ui/helpers/helpers";
import { SimpleMenu } from "@repo/ui/Menu/simpleMenu";
import { SimpleMenuItem } from "@repo/ui/Menu/simpleMenuItem";
import { SimpleMenuPopover } from "@repo/ui/Menu/simpleMenuPopover";
import { useNow } from "@repo/ui/timestamps/timestampContext";
import { type FC, useContext, useState } from "react";
import {
  Calendar,
  RootMenuTriggerStateContext,
  SubmenuTrigger,
} from "react-aria-components";

export interface DateCustomFieldConditionMenuProps {
  customFieldDefinitionId: string;
  fieldType: DateCustomFieldType;
  onSelect: (condition: CustomFieldCondition) => void;
}

export const DateCustomFieldConditionMenu: FC<
  DateCustomFieldConditionMenuProps
> = (props) => {
  return (
    <SimpleMenu aria-label="Date custom field condition">
      <SubmenuTrigger>
        <SimpleMenuItem>is after</SimpleMenuItem>
        <SimpleMenuPopover maxHeight={400}>
          <DateComparisonPicker {...props} operator="after" />
        </SimpleMenuPopover>
      </SubmenuTrigger>

      <SubmenuTrigger>
        <SimpleMenuItem>is before</SimpleMenuItem>
        <SimpleMenuPopover maxHeight={400}>
          <DateComparisonPicker {...props} operator="before" />
        </SimpleMenuPopover>
      </SubmenuTrigger>

      <SimpleMenuItem
        onAction={() =>
          props.onSelect({
            customFieldDefinitionId: props.customFieldDefinitionId,
            fieldType: props.fieldType,
            operator: "within_selected_date_range",
          })
        }
      >
        is within selected date range
      </SimpleMenuItem>

      <SimpleMenuItem
        onAction={() =>
          props.onSelect({
            customFieldDefinitionId: props.customFieldDefinitionId,
            operator: "has_any_value",
          })
        }
      >
        is not empty
      </SimpleMenuItem>
    </SimpleMenu>
  );
};

interface DateComparisonPickerProps extends DateCustomFieldConditionMenuProps {
  operator: "after" | "before";
}

const DateComparisonPicker: FC<DateComparisonPickerProps> = (props) => {
  const rootMenuTriggerState = useContext(RootMenuTriggerStateContext);
  const now = useNow();
  const [value, setValue] = useState(
    () => new CalendarDate(now.year, now.month, now.day),
  );

  const handleApply = () => {
    props.onSelect({
      customFieldDefinitionId: props.customFieldDefinitionId,
      fieldType: props.fieldType,
      operator: props.operator,
      value: value.toString(),
    });
    rootMenuTriggerState?.close();
  };

  return (
    <div className="p-4">
      <Calendar
        aria-label={`Date custom field is ${props.operator}`}
        value={value}
        onChange={setValue}
      >
        <CalendarNavigationHeader />
        <CalendarMonth selectionMode="single" />
      </Calendar>
      <div className="mt-4 flex justify-end">
        <PickerActions onApply={handleApply} />
      </div>
    </div>
  );
};

const PickerActions: FC<{ onApply: () => void }> = ({ onApply }) => (
  <Button size={Size.MEDIUM} variant={ButtonVariant.Primary} onClick={onApply}>
    Apply
  </Button>
);
